import React, { useEffect, useState } from 'react';
import { BanknoteIcon, Wallet } from 'lucide-react';
import TransactionProgress from './TransactionProgress';
import { BASIS_POINTS_DIVISOR, scaleDown } from '../utils/scale';
import { MONOPOLY_DECIMALS } from '../../solana-constants';
import { PayoutConfig } from '../utils/instructions';

interface RollBalanceProps {
  balance: string;
}

const RollBalance: React.FC<RollBalanceProps> = ({ balance }) => (
  <div className="flex items-center gap-2 text-accent-400 bg-gray-700/50 px-4 py-2 rounded-lg">
    <Wallet className="w-4 h-4" />
    <span>{balance} $ROLL</span>
  </div>
);

const MoneyExplosion = () => {
  // Generate random angles and speeds for each particle
  const particles = [...Array(128)].map((_, i) => ({
    angle: Math.random() * 360, // Random angle in degrees
    speed: Math.random() * 2, // Random speed multiplier between 0 and 2
    spinSpeed: 0.5 + Math.random() * 1.5, // Random spin speed
    delay: Math.random() * 0.3, // Random start delay
  }));

  return (
    <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
      <style>
        {particles.map((particle, i) => `
          @keyframes explosion-${i} {
            0% {
              transform: translate(0, 0) rotate(0deg);
              opacity: 1;
            }
            100% {
              transform: translate(
                ${Math.cos(particle.angle * Math.PI / 180) * 100 * particle.speed}px,
                ${Math.sin(particle.angle * Math.PI / 180) * 100 * particle.speed}px
              ) rotate(${particle.angle}deg);
              opacity: 0;
            }
          }
          @keyframes spin-${i} {
            from { transform: rotate(0deg); }
            to { transform: rotate(${360 * particle.spinSpeed}deg); }
          }
        `).join('\n')}
      </style>

      {particles.map((particle, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            animation: `explosion-${i} 2s ${particle.delay}s forwards cubic-bezier(0.16, 1, 0.3, 1)`,
          }}
        >
          <BanknoteIcon
            className="w-8 h-8 text-green-400"
            style={{
              animation: `spin-${i} 1s ${particle.delay}s infinite linear`,
              transformOrigin: 'center',
            }}
          />
        </div>
      ))}
    </div>
  );
};

const EnhancedDiceRollAnimation = ({
  isRolling,
  dice1 = 1,
  dice2 = 1,
  payout,
  rewardPool,
  onRoll,
  hasPlayed,
  transactionStep,
  rollBalance,
}: {
  isRolling: boolean;
  payout: any;
  dice1: number;
  dice2: number;
  rewardPool?: string;
  onRoll: () => void;
  hasPlayed: boolean;
  transactionStep: 'play' | 'settle' | null;
  rollBalance: string;
}) => {
  const [showWinEffect, setShowWinEffect] = useState(false);
  const [rollingNumbers, setRollingNumbers] = useState({ dice1: 1, dice2: 1 });
  const isWin = Boolean(payout);

  useEffect(() => {
    if (isRolling) {
      const interval = setInterval(() => {
        setRollingNumbers({
          dice1: Math.floor(Math.random() * 6) + 1,
          dice2: Math.floor(Math.random() * 6) + 1
        });
      }, 100);

      return () => clearInterval(interval);
    } else if (!isRolling && isWin) {
      setShowWinEffect(true);
      const timer = setTimeout(() => setShowWinEffect(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isRolling, isWin]);


  const getDots = (value: number) => {
    const dots = [];
    switch (value) {
      case 1:
        dots.push('col-start-2 col-end-3 row-start-2 row-end-3');
        break;
      case 2:
        dots.push('col-start-1 row-start-1', 'col-start-3 row-start-3');
        break;
      case 3:
        dots.push('col-start-1 row-start-1', 'col-start-2 row-start-2', 'col-start-3 row-start-3');
        break;
      case 4:
        dots.push('col-start-1 row-start-1', 'col-start-3 row-start-1',
          'col-start-1 row-start-3', 'col-start-3 row-start-3');
        break;
      case 5:
        dots.push('col-start-1 row-start-1', 'col-start-3 row-start-1',
          'col-start-2 row-start-2',
          'col-start-1 row-start-3', 'col-start-3 row-start-3');
        break;
      case 6:
        dots.push('col-start-1 row-start-1', 'col-start-3 row-start-1',
          'col-start-1 row-start-2', 'col-start-3 row-start-2',
          'col-start-1 row-start-3', 'col-start-3 row-start-3');
        break;
      default:
        break;
    }
    return dots;
  };

  const calculateReward = (payout: PayoutConfig | null) => {
    if (!payout) {
      return ""
    }

    if (payout.payout_type === 0) {
      return scaleDown(payout.payout_value.toString(), MONOPOLY_DECIMALS)
    } else {
      const poolSize = parseFloat(rewardPool);
      const percentage = parseFloat((Number(payout.payout_value) / Number(BASIS_POINTS_DIVISOR)).toString());
      const reward = (poolSize * percentage);
      return reward.toFixed(2);
    }
  };
  const payoutValue = calculateReward(payout)

  return (
    <div className="relative flex flex-col items-center">
      {/* Win Effect Container */}
      {showWinEffect && (
        <div className="absolute -inset-16 flex items-center justify-center">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-yellow-500 opacity-20 animate-pulse rounded-full blur-xl" />
          <MoneyExplosion />
        </div>
      )}

      {/* Dice Container */}
      <div className="flex justify-center items-center gap-4 relative z-10">
        {[isRolling ? rollingNumbers.dice1 : dice1,
        isRolling ? rollingNumbers.dice2 : dice2].map((dice, idx) => (
          <div
            key={idx}
            className={`
              w-24 h-24 lg:w-32 lg:h-32 bg-white rounded-xl shadow-xl p-4
              transition-all duration-500
              ${isRolling ? 'animate-bounce' : ''}
              ${!isRolling && isWin ? 'scale-110' : 'scale-100'}
              ${!isRolling && !isWin ? 'scale-95' : ''}
              hover:shadow-2xl
            `}
            style={idx === 1 ? {
              animationDelay: '300ms',
            } : {}}
          >
            <div className={`
              w-full h-full grid grid-cols-3 grid-rows-3 gap-2
              ${showWinEffect ? 'animate-pulse' : ''}
            `}>
              {getDots(dice).map((position, i) => (
                <div
                  key={i}
                  className={`
                    ${position} rounded-full
                    ${isWin ? 'bg-gradient-to-r from-purple-600 via-pink-600 to-yellow-600' : 'bg-primary-600'}
                    ${showWinEffect ? 'animate-pulse' : ''}
                  `}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Win/Lose Text */}
      {hasPlayed && !isRolling && (
        <div className={`
          mt-8 text-center
          transition-all duration-500 transform
          ${isWin ? 'scale-125' : 'scale-100'}
        `}>
          <p className={`
            text-3xl font-bold mb-2
            ${isWin ? 'animate-bounce text-yellow-400' : 'text-gray-400'}
          `}>
            {isWin ? '🎉 Winner! 🎉' : 'No Reward. Roll Again!'}
          </p>
          {isWin && payout && (
            <p className="text-xl text-green-400 font-semibold animate-pulse">
              You won {payoutValue} $MONOPOLY!
            </p>
          )}
        </div>
      )}

      {/* Roll Button and Balance */}
      <div className="flex flex-col items-center gap-4 mt-8">
        {Number(rollBalance) >= 1 && (
          <button
            onClick={onRoll}
            disabled={isRolling}
            className="px-12 py-6 text-2xl font-bold text-white rounded-xl transform transition-all duration-200 
            bg-gradient-to-r from-purple-600 to-secondary-500 animate-pulse hover:animate-none 
            hover:scale-105 disabled:scale-100 disabled:opacity-50 disabled:cursor-not-allowed 
            shadow-lg hover:shadow-xl min-w-[240px]
            relative"
          >
            <div className="flex items-center justify-center gap-3">
              {isRolling ? 'Rolling...' : 'Roll Dice'}
            </div>
          </button>
        )}

        <div className="mt-4">
          <RollBalance balance={rollBalance} />
        </div>
      </div>

      {/* Reward Pool Display */}
      {rewardPool && (
        <div className="mt-4 text-lg text-gray-300">
          Prize Pool: <span className="text-accent-400 font-semibold text-lg">{rewardPool} $MONOPOLY</span>
        </div>
      )}

      {transactionStep && (
        <TransactionProgress currentStep={transactionStep} />
      )}

      <style>{`
        @keyframes explosion {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(-100px) rotate(360deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default EnhancedDiceRollAnimation;
